import React from "react";

import type { ACPStatus } from "@/features/acp/types";
import type { AdaptedPatientConsent } from "@/lib/helpers/adaptConsent";
import { cn } from "@/lib/helpers/cn";
import dayjs, { guessTimezone } from "@/lib/helpers/dayjs";

import { DetailItem } from "./SectionDetail";
import { DownloadButton } from "./DownloadButton";
import { MemberProfileAcpBadge } from "./MemberProfileAcpBadge";

type SectionDetailListProps = {
  consent: AdaptedPatientConsent;
  consentStatus: ACPStatus;
};

export function SectionDetailList(props: SectionDetailListProps) {
  const details = mapStatusToDetails(props);
  const isOptedIn = props.consentStatus === "OPTED_IN";

  return (
    <dl
      className={cn(
        "tw:m-0! tw:grid tw:w-full",
        "tw:max-lg2:grid-cols-2 tw:max-lg2:gap-y-2",
        "tw:lg2:grid-flow-col tw:lg2:items-center tw:lg2:gap-x-3",
        isOptedIn
          ? "tw:lg2:grid-cols-[repeat(4,minmax(200px,1fr))]"
          : "tw:lg2:grid-cols-[repeat(auto-fit,minmax(200px,1fr))]",
      )}
    >
      {details.map((detail) => (
        <DetailItem detail={detail} key={detail.key} />
      ))}
    </dl>
  );
}

function mapStatusToDetails(props: SectionDetailListProps) {
  const { consent, consentStatus } = props;

  const { consentFormEffectiveDate, entityId, responseGivenByName, timestamp, userFileReportKey } =
    consent || {};

  switch (consentStatus) {
    case "OPTED_IN":
      return [
        {
          key: "consent_status",
          label: "Consent Status",
          value: <MemberProfileAcpBadge status={consentStatus} />,
        },
        { key: "given_by", label: "Given By", value: responseGivenByName },
        {
          key: "timestamp",
          label: "Date Agreed",
          value: formatConsentDate(timestamp),
        },
        { key: "effective_date", label: "Effective Date", value: consentFormEffectiveDate },
        {
          key: "download",
          label: "",
          value: (
            <DownloadButton key="download" patientId={entityId} reportKey={userFileReportKey} />
          ),
        },
      ];

    case "OPTED_OUT":
      return [
        {
          key: "consent_status",
          label: "Consent Status",
          value: <MemberProfileAcpBadge status={consentStatus} />,
        },
        { key: "given_by", label: "Given By", value: responseGivenByName },
        {
          key: "timestamp",
          label: "Date Opted Out",
          value: formatConsentDate(timestamp),
        },
        { key: "effective_date", label: "Effective Date", value: consentFormEffectiveDate },
      ];

    case "NOT_RESPONDED":
      return [
        {
          key: "consent_status",
          label: "Consent Status",
          value: <MemberProfileAcpBadge status={consentStatus} />,
        },
        // do not render column for users who did not log in after registering
        ...(consentFormEffectiveDate
          ? [
              {
                key: "effective_date",
                label: "Effective Date",
                value: consentFormEffectiveDate,
              },
            ]
          : []),
      ];

    default:
      // NOT ELIGIBLE
      return [
        {
          key: "consent_status",
          label: "Consent Status",
          value: <MemberProfileAcpBadge status={consentStatus} />,
        },
      ];
  }
}

function formatConsentDate(timestamp?: string) {
  if (!timestamp) return null;

  return dayjs.utc(timestamp).tz(guessTimezone()).format("M/D/YYYY hh:mm a");
}
